import React, { useState, useEffect, useCallback, useContext } from "react";
import { Card, Row, Col, Table, Button, Badge, Spinner, Modal } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { PageTitle } from "../../../_metronic/layout/core";
import { genericGetOne, getImagesForEntry } from "../generic/crud";
import { ENTITY_ID_RAW_PURCHASE } from "../generic/Constants";
import { API_URL } from "../../config/api.config";
import { showToast, TOAST_FAILURE } from "../../utils/toast/Toast";
import BedRockContext from "../../BedRockContext";
import { PurchaseDumpLocationCards } from "./PurchaseDumpLocationCards";
import { formatDateTime } from "../../utils/Utils";

// Format currency
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2
    }).format(amount);
};

export function PurchaseViewPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const myData = useContext(BedRockContext);
    const [isLoading, setIsLoading] = useState(true);
    const [purchaseData, setPurchaseData] = useState(null);
    const [capturedImages, setCapturedImages] = useState([]);
    const [imagesLoading, setImagesLoading] = useState(false);

    // Clear toolbar buttons on mount
    useEffect(() => {
        myData.setBaseData({
            toolBarButtons: [],
        });
    }, []);

    // Breadcrumbs
    const breadcrumbs = [
        { title: "Home", path: "/dashboard", isSeparator: false, isActive: false },
        { title: "", path: "", isSeparator: true, isActive: false },
        { title: "Purchases", path: "/purchases-list", isSeparator: false, isActive: false },
        { title: "", path: "", isSeparator: true, isActive: false },
    ];

    // Load purchase data
    useEffect(() => {
        const loadPurchase = async () => {
            if (!id) return;

            try {
                setIsLoading(true);
                const { data } = await genericGetOne(ENTITY_ID_RAW_PURCHASE, id);

                if (data) {
                    setPurchaseData(data);
                }
            } catch (error) {
                showToast("Error loading purchase", TOAST_FAILURE);
                navigate('/purchases-list');
            } finally {
                setIsLoading(false);
            }
        };

        loadPurchase();
    }, [id, navigate]);

    // Load captured images for this purchase
    useEffect(() => {
        const entryPrimaryId = purchaseData?.primaryId || purchaseData?.id || id;
        if (entryPrimaryId) {
            setImagesLoading(true);
            getImagesForEntry('PURCHASE', entryPrimaryId)
                .then((response) => {
                    const data = response?.data;
                    if (data?.success && data?.images) {
                        setCapturedImages(data.images);
                    }
                })
                .catch((error) => {
                    console.error("Error loading images:", error);
                })
                .finally(() => {
                    setImagesLoading(false);
                });
        }
    }, [purchaseData?.primaryId, purchaseData?.id, id]);

    const formatDate = useCallback((dateStr) => {
        return formatDateTime(dateStr);
    }, []);

    const handleBack = () => {
        navigate('/purchases-list');
    };

    const handleEdit = () => {
        navigate(`/purchase-new/edit/${id}`);
    };

    const handlePrint = () => {
        navigate(`/purchase-print/${id}`);
    };

    // Calculate totals from itemsList
    const calculateTotals = useCallback((items) => {
        if (!items || items.length === 0) {
            return { subtotal: 0, totalDiscount: 0, totalTax: 0, grandTotal: 0, taxBreakdown: {} };
        }

        let subtotal = 0;
        let totalDiscount = 0;
        let totalTax = 0;
        const taxBreakdown = {};

        items.forEach(item => {
            const price = parseFloat(item.price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            const discount = parseFloat(item.discountAmount) || 0;
            const grossAmount = price * quantity;
            const itemSubtotal = grossAmount - discount;
            subtotal += grossAmount;
            totalDiscount += discount;

            // Calculate taxes (on amount after discount)
            if (item.taxPlans && item.taxPlans.length > 0) {
                item.taxPlans.forEach(tax => {
                    // Extract percentage from name like "GST (11%)" or use direct percentage
                    let taxPercent = tax.percentage || tax.taxPercentage || 0;
                    if (!taxPercent && tax.name) {
                        const percentMatch = tax.name.match(/\((\d+(?:\.\d+)?)\s*%\)/);
                        taxPercent = percentMatch ? parseFloat(percentMatch[1]) : 0;
                    }
                    const taxAmount = (itemSubtotal * taxPercent) / 100;
                    totalTax += taxAmount;

                    // Add to breakdown
                    const taxKey = tax.name || `Tax (${taxPercent}%)`;
                    if (!taxBreakdown[taxKey]) {
                        taxBreakdown[taxKey] = 0;
                    }
                    taxBreakdown[taxKey] += taxAmount;
                });
            }
        });

        const roundOff = parseFloat(purchaseData?.roundOffAmount) || 0;
        const grandTotal = (subtotal - totalDiscount) + totalTax + roundOff;

        return { subtotal, totalDiscount, totalTax, grandTotal, taxBreakdown };
    }, [purchaseData?.roundOffAmount]);

    // Calculate item tax amount
    const calculateItemTax = (item) => {
        const price = parseFloat(item.price) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const discount = parseFloat(item.discountAmount) || 0;
        const itemSubtotal = (price * quantity) - discount;

        let itemTax = 0;
        if (item.taxPlans && item.taxPlans.length > 0) {
            item.taxPlans.forEach(tax => {
                let taxPercent = tax.percentage || tax.taxPercentage || 0;
                if (!taxPercent && tax.name) {
                    const percentMatch = tax.name.match(/\((\d+(?:\.\d+)?)\s*%\)/);
                    taxPercent = percentMatch ? parseFloat(percentMatch[1]) : 0;
                }
                itemTax += (itemSubtotal * taxPercent) / 100;
            });
        }
        return itemTax;
    };

    if (isLoading) {
        return (
            <>
                <PageTitle breadcrumbs={breadcrumbs}>View Purchase</PageTitle>
                <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                    <Spinner animation="border" variant="primary" />
                    <span className="ms-2">Loading purchase data...</span>
                </div>
            </>
        );
    }

    if (!purchaseData) {
        return (
            <>
                <PageTitle breadcrumbs={breadcrumbs}>View Purchase</PageTitle>
                <div className="text-center py-5">
                    <i className="bi bi-exclamation-triangle fs-1 text-warning mb-3"></i>
                    <h4>Purchase not found</h4>
                    <Button variant="primary" onClick={handleBack}>
                        Back to Purchases
                    </Button>
                </div>
            </>
        );
    }

    const items = purchaseData.itemsList || [];
    const totals = calculateTotals(items);
    const totalQuantity = items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);

    return (
        <>
            <PageTitle breadcrumbs={breadcrumbs}>View Purchase</PageTitle>
            <div className="container-fluid px-0">
                <Row>
                    {/* LEFT COLUMN - Primary Purchase Data */}
                    <Col lg={8}>
                        {/* Purchase Information Card */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <Card.Header className="py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-receipt me-2" style={{ color: '#4a90a4' }}></i>
                                    <span className="fw-semibold small" style={{ color: '#4a90a4' }}>PURCHASE INFORMATION</span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <label className="form-label text-muted small">Ticket No.</label>
                                            <div className="fw-semibold fs-5">{purchaseData.purchaseTicketNumber || 'N/A'}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <label className="form-label text-muted small">Supplier</label>
                                            <div className="fw-semibold">{purchaseData.supplier?.name || '-'}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <label className="form-label text-muted small">Vehicle</label>
                                            <div className="fw-semibold">{purchaseData.vehicleNumber || '-'}</div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <label className="form-label text-muted small">Purchase Date</label>
                                            <div className="fw-semibold">{formatDate(purchaseData.purchaseDate)}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <label className="form-label text-muted small">Driver Name</label>
                                            <div className="fw-semibold">{purchaseData.driverName || '-'}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <label className="form-label text-muted small">Batta Amount</label>
                                            <div className="fw-semibold">{purchaseData.battaAmount ? formatCurrency(purchaseData.battaAmount) : '-'}</div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Purchase Items Card */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <Card.Header className="py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-basket me-2" style={{ color: '#4a90a4' }}></i>
                                        <span className="fw-semibold small" style={{ color: '#4a90a4' }}>PURCHASE ITEMS</span>
                                        {items.length > 0 && (
                                            <Badge bg="primary" className="ms-2">{items.length}</Badge>
                                        )}
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {items.length > 0 ? (
                                    <Table hover className="mb-0" size="sm">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="ps-3 py-2" style={{ width: '30%' }}>Item</th>
                                                <th className="py-2 text-end" style={{ width: '15%' }}>Rate</th>
                                                <th className="py-2 text-center" style={{ width: '10%' }}>Qty</th>
                                                <th className="py-2 text-center" style={{ width: '10%' }}>Disc</th>
                                                <th className="py-2 text-center" style={{ width: '18%' }}>Tax</th>
                                                <th className="py-2 text-end pe-3" style={{ width: '17%' }}>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((item, index) => {
                                                const price = parseFloat(item.price) || 0;
                                                const quantity = parseFloat(item.quantity) || 0;
                                                const discount = parseFloat(item.discountAmount) || 0;
                                                const itemSubtotal = (price * quantity) - discount;
                                                const itemTax = calculateItemTax(item);
                                                const lineTotal = itemSubtotal + itemTax;

                                                return (
                                                    <tr key={item.id || index} className={index === 0 ? "bg-primary bg-opacity-10" : ""}>
                                                        <td className="ps-3 py-2">
                                                            <div className="d-flex align-items-center">
                                                                <span className="fw-semibold">{item.inventoryItemName}</span>
                                                                {item.unitSymbol && (
                                                                    <small className="text-muted ms-1">({item.unitSymbol})</small>
                                                                )}
                                                            </div>
                                                            {item.hsnCode && (
                                                                <small className="text-muted">HSN: {item.hsnCode}</small>
                                                            )}
                                                        </td>
                                                        <td className="py-2 text-end">
                                                            <span className="text-success fw-semibold">{formatCurrency(price)}</span>
                                                        </td>
                                                        <td className="py-2 text-center">
                                                            <span className="fw-semibold">{quantity}</span>
                                                        </td>
                                                        <td className="py-2 text-center">
                                                            <span className={discount > 0 ? "text-danger fw-semibold" : "text-muted"}>
                                                                {discount > 0 ? formatCurrency(discount) : '-'}
                                                            </span>
                                                        </td>
                                                        <td className="py-2 text-center">
                                                            {item.taxPlans && item.taxPlans.length > 0 ? (
                                                                <span className="text-warning fw-semibold">
                                                                    {formatCurrency(itemTax)}
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td className="py-2 text-end pe-3">
                                                            <span className="fw-semibold text-primary">{formatCurrency(lineTotal)}</span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <div className="text-center py-4 text-muted">
                                        <i className="bi bi-basket fs-2 d-block mb-2 opacity-50"></i>
                                        <p className="mb-0 small">No items in this purchase</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Bill Summary Card */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <Card.Header className="py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-calculator me-2" style={{ color: '#4a90a4' }}></i>
                                    <span className="fw-semibold small" style={{ color: '#4a90a4' }}>BILL SUMMARY</span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-3">
                                {items.length > 0 ? (
                                    <div className="bill-summary">
                                        <div className="border-top pt-3">
                                            {/* Subtotal */}
                                            <div className="d-flex justify-content-between py-2">
                                                <span className="text-muted">Subtotal:</span>
                                                <span className="fw-semibold">{formatCurrency(totals.subtotal)}</span>
                                            </div>

                                            {/* Item Discounts */}
                                            {totals.totalDiscount > 0 && (
                                                <div className="d-flex justify-content-between py-2" style={{ borderTop: '1px dotted #e9ecef' }}>
                                                    <span className="text-danger">Item Discounts:</span>
                                                    <span className="text-danger">-{formatCurrency(totals.totalDiscount)}</span>
                                                </div>
                                            )}

                                            {/* Tax Breakdown */}
                                            {Object.keys(totals.taxBreakdown).length > 0 && (
                                                <div style={{ borderTop: '1px dotted #e9ecef' }}>
                                                    {Object.entries(totals.taxBreakdown).map(([taxName, taxAmount], index) => (
                                                        <div key={index} className="d-flex justify-content-between py-2">
                                                            <span className="text-warning">{taxName}:</span>
                                                            <span className="text-warning">{formatCurrency(taxAmount)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Round Off */}
                                            {purchaseData.roundOffAmount !== 0 && purchaseData.roundOffAmount !== undefined && (
                                                <div className="d-flex justify-content-between py-2" style={{ borderTop: '1px dotted #e9ecef' }}>
                                                    <span className={purchaseData.roundOffAmount >= 0 ? 'text-success' : 'text-danger'}>
                                                        Round Off:
                                                    </span>
                                                    <span className={purchaseData.roundOffAmount >= 0 ? 'text-success' : 'text-danger'}>
                                                        {purchaseData.roundOffAmount >= 0 ? '+' : ''}{formatCurrency(purchaseData.roundOffAmount)}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Grand Total */}
                                            <div
                                                className="d-flex justify-content-between align-items-center py-3 mt-2"
                                                style={{
                                                    borderTop: '2px double #333',
                                                    borderBottom: '2px double #333',
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                            >
                                                <span className="fw-bold fs-5">Grand Total:</span>
                                                <span className="fw-bold fs-4 text-success">{formatCurrency(totals.grandTotal)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-3">
                                        <i className="bi bi-receipt fs-1 d-block mb-2 opacity-50"></i>
                                        <p className="mb-0">No items to summarize</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Dump Location - only when set */}
                        {purchaseData.dumpLocation && (
                            <PurchaseDumpLocationCards
                                value={purchaseData.dumpLocation}
                                onChange={() => {}}
                                readOnly={true}
                            />
                        )}
                    </Col>

                    {/* RIGHT COLUMN - Additional Info & Actions */}
                    <Col lg={4}>
                        {/* Weight Information Panel */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <Card.Header className="py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-speedometer me-2" style={{ color: '#4a90a4' }}></i>
                                    <span className="fw-semibold small" style={{ color: '#4a90a4' }}>WEIGHT INFORMATION</span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-3">
                                <Row className="g-2">
                                    <Col xs={6}>
                                        <div className="text-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Empty Weight</small>
                                            <span className="fw-bold" style={{ fontSize: '1.1rem', color: purchaseData.emptyWeight ? '#0d6efd' : '#6c757d' }}>
                                                {purchaseData.emptyWeight ? `${purchaseData.emptyWeight} Ton` : '-- Ton'}
                                            </span>
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="text-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Loaded Weight</small>
                                            <span className="fw-bold" style={{ fontSize: '1.1rem', color: purchaseData.loadedWeight ? '#198754' : '#6c757d' }}>
                                                {purchaseData.loadedWeight ? `${purchaseData.loadedWeight} Ton` : '-- Ton'}
                                            </span>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Correction (wastage) */}
                                {(parseFloat(purchaseData.loadedWeightCorrection) || 0) > 0 && (() => {
                                    const correction = parseFloat(purchaseData.loadedWeightCorrection);
                                    const effective = (parseFloat(purchaseData.loadedWeight) || 0) - correction;
                                    return (
                                        <div className="mt-2 p-2 rounded" style={{ backgroundColor: '#fff', border: '1px solid #dee2e6' }}>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <small className="text-muted">
                                                    <i className="bi bi-dash-circle me-1"></i>Correction (wastage)
                                                </small>
                                                <span className="fw-semibold" style={{ fontSize: '0.95rem', color: '#dc3545' }}>
                                                    {correction.toFixed(3)} Ton
                                                </span>
                                            </div>
                                            <div className="text-end mt-1">
                                                <small className="text-success fw-semibold">
                                                    Effective Loaded: {effective.toFixed(3)} Ton
                                                </small>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Net Weight Display */}
                                {(() => {
                                    const correction = parseFloat(purchaseData.loadedWeightCorrection) || 0;
                                    const netWeight = (parseFloat(purchaseData.loadedWeight) || 0) - correction - (parseFloat(purchaseData.emptyWeight) || 0);
                                    return (
                                        <div className="mt-3 p-2 rounded text-center" style={{ backgroundColor: netWeight > 0 ? '#d1e7dd' : '#f8f9fa', border: netWeight > 0 ? '1px solid #badbcc' : '1px solid #dee2e6' }}>
                                            <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Net Weight</small>
                                            <span className="fw-bold" style={{ fontSize: '1.4rem', color: netWeight > 0 ? '#198754' : '#6c757d' }}>
                                                {netWeight > 0 ? `${netWeight.toFixed(3)} Ton` : '-- Ton'}
                                            </span>
                                        </div>
                                    );
                                })()}
                            </Card.Body>
                        </Card>

                        {/* Captured Images */}
                        {(capturedImages.length > 0 || imagesLoading) && (
                            <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                                <Card.Header className="py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-camera me-2" style={{ color: '#4a90a4' }}></i>
                                        <span className="fw-semibold small" style={{ color: '#4a90a4' }}>CAPTURED IMAGES</span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    {imagesLoading ? (
                                        <div className="text-center py-3">
                                            <Spinner animation="border" size="sm" variant="primary" />
                                            <small className="text-muted d-block mt-2">Loading images...</small>
                                        </div>
                                    ) : (
                                        <Row className="g-2 justify-content-center">
                                            {capturedImages.map((image, index) => {
                                                const label = image.imageType === 'LOADED_WEIGHT' ? 'Loaded Weight' :
                                                              image.imageType === 'EMPTY_WEIGHT' ? 'Empty Weight' :
                                                              image.imageType || 'Image';
                                                return (
                                                    <Col xs={capturedImages.length === 1 ? 12 : 6} key={image.primaryId || index}>
                                                        <PurchaseImageThumb image={image} label={label} />
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Order Summary Panel */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <Card.Header className="py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-receipt me-2" style={{ color: '#4a90a4' }}></i>
                                    <span className="fw-semibold small" style={{ color: '#4a90a4' }}>ORDER SUMMARY</span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-3">
                                <Row className="g-2 mb-3">
                                    <Col xs={6}>
                                        <div className="text-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Total Items</small>
                                            <span className="fw-bold" style={{ fontSize: '1.3rem', color: '#0d6efd' }}>
                                                {items.length}
                                            </span>
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="text-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Total Quantity</small>
                                            <span className="fw-bold" style={{ fontSize: '1.3rem', color: '#198754' }}>
                                                {totalQuantity}
                                            </span>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Grand Total Display */}
                                <div className="p-3 rounded text-center" style={{ backgroundColor: '#d1e7dd', border: '1px solid #badbcc' }}>
                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Grand Total</small>
                                    <span className="fw-bold" style={{ fontSize: '1.6rem', color: '#198754' }}>
                                        {formatCurrency(totals.grandTotal)}
                                    </span>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Additional Details Card */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <Card.Header className="py-2 px-4" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-info-circle me-2" style={{ color: '#4a90a4' }}></i>
                                    <span className="fw-semibold small" style={{ color: '#4a90a4' }}>ADDITIONAL DETAILS</span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-3">
                                <Row className="g-2">
                                    <Col xs={12}>
                                        <div className="mb-2">
                                            <label className="form-label text-muted small mb-0">Inspection Notes</label>
                                            <div className="fw-semibold small">{purchaseData.inspectionNotes || '-'}</div>
                                        </div>
                                    </Col>
                                    <Col xs={12}>
                                        <div className="mb-2">
                                            <label className="form-label text-muted small mb-0">Remarks</label>
                                            <div className="fw-semibold small">{purchaseData.remarks || '-'}</div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Action Buttons */}
                        <div className="d-grid gap-2">
                            <Button
                                variant="light"
                                onClick={handlePrint}
                                size="lg"
                                className="text-success"
                                style={{ border: '1px solid #198754', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
                            >
                                <i className="bi bi-printer me-2"></i>
                                View Print Ticket
                            </Button>
                            <Button
                                variant="light"
                                onClick={handleBack}
                                size="lg"
                                className="text-secondary"
                                style={{ border: '1px solid #ced4da', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
                            >
                                <i className="bi bi-arrow-left me-2"></i>
                                Back to Purchases List
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>
        </>
    );
}

function PurchaseImageThumb({ image, label }) {
    const [showModal, setShowModal] = useState(false);
    const src = `${API_URL}/api/vehicle-capture/image/${image.primaryId}`;

    return (
        <>
            <div className="border rounded overflow-hidden"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowModal(true)}>
                <img
                    src={src}
                    alt={label}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                    }}
                />
                <div className="d-none align-items-center justify-content-center bg-light text-muted"
                    style={{ height: '100px' }}>
                    <i className="bi bi-image"></i>
                </div>
                <div className="px-2 py-1 bg-light text-center">
                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>{label}</small>
                    {image.capturedAt && (
                        <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                            {formatDateTime(image.capturedAt)}
                        </small>
                    )}
                </div>
            </div>
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                <Modal.Header closeButton className="py-2">
                    <Modal.Title style={{ fontSize: '14px' }}>
                        <i className="bi bi-image me-2"></i>{label} Image
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-2">
                    <img src={src} alt={label}
                        className="rounded"
                        style={{ width: '100%', height: 'auto', display: 'block' }} />
                </Modal.Body>
                <Modal.Footer className="border-0 py-2">
                    <a href={src} target="_blank" rel="noopener noreferrer"
                        className="btn btn-outline-primary btn-sm">
                        <i className="bi bi-box-arrow-up-right me-1"></i>Open Full Size
                    </a>
                    <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
